/**
 * Client-side checks run before a workflow file is uploaded.
 *
 * These are a convenience, never the authority: the service validates every
 * upload and its verdict is the one that counts (FR-012). What they buy is a
 * pointless round trip avoided and an immediate answer for the three things a
 * browser can see on its own — the extension, that the file is not empty, and
 * that it is not absurdly large.
 *
 * Pure and synchronous so the dialog can call it during render without
 * ceremony, and so these rules are testable without a network or a component.
 */

/** Mirrors the service's default limit; it enforces its own regardless. */
export const MAX_UPLOAD_BYTES = 1024 * 1024;

/** A bundle's limit (service `BUNDLE__MAX_BYTES`, spec 204); enforced there too. */
export const MAX_BUNDLE_BYTES = 50 * 1024 * 1024;

const ACCEPTED_EXTENSIONS = ['.yaml', '.yml'];
const BUNDLE_EXTENSION = '.zip';

/** A workflow bundle (spec 204) rather than one workflow's YAML. */
export function isBundle(file: File): boolean {
  return file.name.toLowerCase().endsWith(BUNDLE_EXTENSION);
}

export type PreCheckFailure = {
  reason: 'extension' | 'empty' | 'too_large';
  message: string;
};

function formatMegabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
}

/**
 * Returns the first reason the file cannot be uploaded, or `null` if the
 * browser has no objection.
 */
export function checkWorkflowFile(file: File): PreCheckFailure | null {
  const name = file.name.toLowerCase();
  const bundle = isBundle(file);
  if (
    !bundle &&
    !ACCEPTED_EXTENSIONS.some((extension) => name.endsWith(extension))
  ) {
    return {
      reason: 'extension',
      message: `Only ${ACCEPTED_EXTENSIONS.join(' and ')} files, or a ${BUNDLE_EXTENSION} bundle, are accepted.`,
    };
  }
  if (file.size === 0) {
    return { reason: 'empty', message: 'This file is empty.' };
  }
  const limit = bundle ? MAX_BUNDLE_BYTES : MAX_UPLOAD_BYTES;
  if (file.size > limit) {
    return {
      reason: 'too_large',
      message: `This file is larger than the ${formatMegabytes(limit)} limit.`,
    };
  }
  return null;
}
